import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { salonApi, staffApi } from '../services/api';
import { useAppLayout } from '../components/AppLayout';
import './SettingsPage.css';

const DAYS = [
  { value: 1, label: 'Δευτέρα' },  { value: 2, label: 'Τρίτη' },
  { value: 3, label: 'Τετάρτη' },  { value: 4, label: 'Πέμπτη' },
  { value: 5, label: 'Παρασκευή' },{ value: 6, label: 'Σάββατο' },
  { value: 0, label: 'Κυριακή' }
];

const formatTime12 = (h, m) => {
  const suffix = h < 12 ? 'ΠΜ' : 'ΜΜ';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
};

const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++)
    for (let m = 0; m < 60; m += 15)
      opts.push({
        val: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`,
        label: formatTime12(h, m)
      });
  return opts;
})();

const COLORS = ['#534AB7', '#E74C3C', '#2ECC71', '#F39C12', '#3498DB', '#9B59B6', '#E91E63', '#00BCD4'];

const DEFAULT_SCHEDULE = {
  1: { enabled: true,  start: '09:00', end: '20:00' },
  2: { enabled: true,  start: '09:00', end: '20:00' },
  3: { enabled: true,  start: '09:00', end: '20:00' },
  4: { enabled: true,  start: '09:00', end: '20:00' },
  5: { enabled: true,  start: '09:00', end: '20:00' },
  6: { enabled: true,  start: '09:00', end: '20:00' },
  0: { enabled: false, start: '09:00', end: '20:00' }
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const { openSidebar } = useAppLayout();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState(null);
  const [form, setForm] = useState({
    name: '', phone: '', address: '', city: '',
    working_schedule: DEFAULT_SCHEDULE
  });

  const [staffList, setStaffList]     = useState([]);
  const [newStaff, setNewStaff]       = useState({ name: '', specialty: '', color: COLORS[0] });
  const [staffSaving, setStaffSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      salonApi.getSettings(),
      staffApi.getAll()
    ]).then(([sRes, stRes]) => {
      const s = sRes.data.salon;
      setForm({
        name: s.name || '', phone: s.phone || '',
        address: s.address || '', city: s.city || '',
        working_schedule: s.working_schedule || DEFAULT_SCHEDULE
      });
      if (stRes.data.staff) setStaffList(stRes.data.staff);
    })
    .catch(() => setError('Σφάλμα φόρτωσης'))
    .finally(() => setLoading(false));
  }, []);

  const toggleDay = (v) =>
    setForm(f => ({
      ...f,
      working_schedule: {
        ...f.working_schedule,
        [v]: { ...f.working_schedule[v], enabled: !f.working_schedule[v].enabled }
      }
    }));

  const updateTime = (v, field, val) =>
    setForm(f => ({
      ...f,
      working_schedule: {
        ...f.working_schedule,
        [v]: { ...f.working_schedule[v], [field]: val }
      }
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setSuccess(false); setError(null);
    try {
      await salonApi.updateSettings(form);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError('Σφάλμα αποθήκευσης');
    } finally { setSaving(false); }
  };

  const addStaff = async () => {
    if (!newStaff.name.trim()) return;
    setStaffSaving(true);
    try {
      const res = await staffApi.create(newStaff);
      setStaffList(prev => [...prev, res.data.staff]);
      setNewStaff({ name: '', specialty: '', color: COLORS[(staffList.length + 1) % COLORS.length] });
    } catch { setError('Σφάλμα προσθήκης'); }
    finally { setStaffSaving(false); }
  };

  const removeStaff = async (id) => {
    try {
      await staffApi.delete(id);
      setStaffList(prev => prev.filter(s => s.id !== id));
    } catch { setError('Σφάλμα διαγραφής'); }
  };

  if (loading) return <div className="settings-loading">Φόρτωση...</div>;

  return (
    <div className="settings-page">
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="settings-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="hamburger-btn" onClick={openSidebar}>☰</button>
          <button className="back-btn" onClick={() => navigate(-1)}>← Πίσω</button>
        </div>
        <h1 className="settings-title">⚙️ Ρυθμίσεις</h1>
      </div>

      <form className="settings-form" onSubmit={handleSubmit}>

        {/* ── Στοιχεία κομμωτηρίου ─────────────────────────── */}
        <div className="settings-section">
          <h2 className="section-title">📋 Στοιχεία κομμωτηρίου</h2>
          <div className="field-grid">
            {[
              { key: 'name',    label: 'Όνομα',       ph: 'Κομμωτήριο Ελένη' },
              { key: 'phone',   label: 'Τηλέφωνο',    ph: '2101234567', type: 'tel' },
              { key: 'city',    label: 'Πόλη',         ph: 'Αθήνα' },
              { key: 'address', label: 'Διεύθυνση',   ph: 'Ερμού 10' }
            ].map(({ key, label, ph, type }) => (
              <div className="field" key={key}>
                <label>{label}</label>
                <input type={type || 'text'} value={form[key]}
                  placeholder={`π.χ. ${ph}`}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
              </div>
            ))}
          </div>
        </div>

        {/* ── Προσωπικό ────────────────────────────────────── */}
        <div className="settings-section">
          <h2 className="section-title">👥 Προσωπικό</h2>

          {staffList.length > 0 && (
            <div className="staff-grid">
              {staffList.map(s => (
                <div key={s.id} className="staff-card-fancy">
                  <div className="staff-card-header">
                    <div className="staff-avatar" style={{ background: s.color }}>
                      {s.name.charAt(0).toUpperCase()}
                    </div>
                    <button type="button" className="staff-remove-x" onClick={() => removeStaff(s.id)}>×</button>
                  </div>
                  <div className="staff-card-body">
                    <div className="staff-name-fancy">{s.name}</div>
                    {s.specialty && <div className="staff-specialty-fancy">{s.specialty}</div>}
                  </div>
                  <div className="staff-card-footer">
                    <div className="staff-color-indicator" style={{ background: s.color }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="staff-add-box">
            <div className="staff-add-title">Προσθήκη νέου υπαλλήλου</div>
            <div className="staff-add-inputs">
              <input type="text" placeholder="Όνομα" value={newStaff.name}
                onChange={e => setNewStaff(n => ({ ...n, name: e.target.value }))} />
              <input type="text" placeholder="Ειδικότητα (π.χ. Κομμωτής)" value={newStaff.specialty}
                onChange={e => setNewStaff(n => ({ ...n, specialty: e.target.value }))} />
            </div>
            <div className="staff-add-color-row">
              <span className="color-label">Χρώμα:</span>
              <div className="color-picker-inline">
                {COLORS.map(c => (
                  <button key={c} type="button"
                    className={'color-dot-inline' + (newStaff.color === c ? ' active' : '')}
                    style={{ background: c }}
                    onClick={() => setNewStaff(n => ({ ...n, color: c }))} />
                ))}
              </div>
              <button type="button" className="btn-add-staff"
                onClick={addStaff} disabled={staffSaving || !newStaff.name.trim()}>
                {staffSaving ? '...' : '+ Προσθήκη'}
              </button>
            </div>
          </div>
        </div>

        {/* ── Ωράριο ───────────────────────────────────────── */}
        <div className="settings-section">
          <h2 className="section-title">⏰ Ωράριο λειτουργίας</h2>
          <div className="schedule-grid">
            {DAYS.map(day => {
              const ds = form.working_schedule[day.value] || { enabled: false, start: '09:00', end: '20:00' };
              return (
                <div key={day.value} className={'schedule-row' + (ds.enabled ? ' enabled' : '')}>
                  <div className="schedule-day">
                    <button type="button" className={'day-toggle' + (ds.enabled ? ' active' : '')}
                      onClick={() => toggleDay(day.value)}>
                      {ds.enabled ? '✓' : '✕'}
                    </button>
                    <span className="day-name">{day.label}</span>
                  </div>
                  {ds.enabled ? (
                    <div className="schedule-times">
                      <div className="time-field">
                        <label>Από</label>
                        <select value={ds.start} onChange={e => updateTime(day.value, 'start', e.target.value)}>
                          {TIME_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                        </select>
                      </div>
                      <span className="time-sep">–</span>
                      <div className="time-field">
                        <label>Έως</label>
                        <select value={ds.end} onChange={e => updateTime(day.value, 'end', e.target.value)}>
                          {TIME_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
                        </select>
                      </div>
                    </div>
                  ) : (
                    <span className="schedule-closed">Κλειστό</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {success && <div className="alert alert-success">✅ Αποθηκεύτηκε!</div>}
        {error   && <div className="alert alert-error">❌ {error}</div>}

        <button type="submit" className="save-btn" disabled={saving}>
          {saving ? 'Αποθήκευση...' : '💾 Αποθήκευση'}
        </button>
      </form>
    </div>
  );
}